import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SettingsSection } from "@/lib/types";
import {
  ADMIN_SETTINGS_GROUPS,
  EMPLOYEE_SETTINGS_GROUPS,
  SettingsNav,
  type SettingsNavGroup,
} from "@/components/settings/SettingsNav";
import { BusinessProfile } from "@/components/settings/sections/BusinessProfile";
import { SubscriptionBilling } from "@/components/settings/sections/SubscriptionBilling";
import { NotificationsAlerts } from "@/components/settings/sections/NotificationsAlerts";
import { TeamAccess } from "@/components/settings/sections/TeamAccess";
import { Permissions } from "@/components/settings/sections/Permissions";
import { SecuritySettings } from "@/components/settings/sections/SecuritySettings";
import { AppAppearance } from "@/components/settings/sections/AppAppearance";
import { SupportSafety } from "@/components/settings/sections/SupportSafety";
import { Legal } from "@/components/settings/sections/Legal";
import { DangerZone } from "@/components/settings/sections/DangerZone";
import { EmployeeAccountSettings } from "@/components/settings/sections/EmployeeAccountSettings";
import { EmployeeNotificationSettings } from "@/components/settings/sections/EmployeeNotificationSettings";

function getAllowedSections(groups: SettingsNavGroup[]) {
  return groups.flatMap((group) => group.items.map((item) => item.id));
}

function isSettingsSection(
  s: string | undefined,
  allowedSections: SettingsSection[],
): s is SettingsSection {
  return !!s && allowedSections.includes(s as SettingsSection);
}

export default function SettingsPage() {
  const { role, loading } = useAuth();
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const isEmployee = role === "employee";
  const groups = isEmployee ? EMPLOYEE_SETTINGS_GROUPS : ADMIN_SETTINGS_GROUPS;
  const allowedSections = getAllowedSections(groups);
  const active = isSettingsSection(section, allowedSections) ? section : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (section && active === null) {
    return <Navigate to="/settings" replace />;
  }

  const handleSectionSelect = (s: SettingsSection) => {
    navigate(`/settings/${s}`, { replace: active === null });
  };

  const renderSection = () => {
    if (isEmployee) {
      switch (active) {
        case "account": return <EmployeeAccountSettings />;
        case "notifications": return <EmployeeNotificationSettings />;
        case "appearance": return <AppAppearance />;
        default: return null;
      }
    }

    switch (active) {
      case "account": return <EmployeeAccountSettings />;
      case "profile": return <BusinessProfile />;
      case "billing": return <SubscriptionBilling />;
      case "notifications": return <NotificationsAlerts />;
      case "team": return <TeamAccess />;
      case "permissions": return <Permissions />;
      case "security": return <SecuritySettings />;
      case "appearance": return <AppAppearance />;
      case "support": return <SupportSafety />;
      case "legal": return <Legal />;
      case "danger": return <DangerZone />;
      default: return null;
    }
  };

  // Drill-down: when a section is selected, show full-page content with Back button
  if (active) {
    const titles: Record<SettingsSection, string> = {
      account: "My Account",
      profile: "Business Profile",
      billing: "Subscription & Billing",
      notifications: "Notifications & Alerts",
      team: "Team & Access",
      permissions: "Permissions",
      security: "Security",
      appearance: "App Appearance",
      support: "Support & Safety",
      legal: "Legal",
      danger: "Danger Zone",
    };
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} aria-label="Back to Settings">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-display font-bold">{titles[active]}</h1>
            <p className="text-sm text-muted-foreground">Settings</p>
          </div>
        </div>
        <div className="pb-20">{renderSection()}</div>
      </div>
    );
  }

  // List view: Snapchat-style grouped nav
  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-display font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground">
          {isEmployee ? "Manage your workspace and account." : "Manage your business and account."}
        </p>
      </div>
      <SettingsNav active={null} onSelect={handleSectionSelect} groups={groups} />
    </div>
  );
}
